import { Amplify } from 'aws-amplify';
import { getCurrentUser, fetchAuthSession, signIn, signOut as amplifySignOut, signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { ensureCognitoConfig } from '@/lib/cognito';

const cfg = ensureCognitoConfig();

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cfg.userPoolId,
      userPoolClientId: cfg.userPoolClientId,
      ...(cfg.userPoolEndpoint
        ? { userPoolEndpoint: cfg.userPoolEndpoint }
        : {}),
      ...(cfg.cognitoDomain
        ? {
            loginWith: {
              oauth: {
                domain: cfg.cognitoDomain,
                scopes: ['email', 'profile', 'openid'],
                redirectSignIn: [window.location.origin],
                redirectSignOut: [window.location.origin],
                responseType: 'code',
              },
            },
          }
        : {}),
    },
  },
});

export interface CognitoUser {
  userId: string;
  email: string;
  emailVerified: boolean;
  groups: string[];
}

export interface AuthState {
  user: CognitoUser | null;
  idToken: string | null;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: Error | null; needsConfirmation?: boolean; idToken?: string | null }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: Error | null; needsConfirmation?: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<{ error: Error | null }>;
  resendConfirmationCode: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function isJwtExpired(token: string, bufferMs = 30 * 1000): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000 - bufferMs;
  } catch {
    return true;
  }
}

async function extractUserFromSession(): Promise<{ user: CognitoUser | null; idToken: string | null }> {
  try {
    const user = await getCurrentUser();

    let session;
    try {
      session = await Promise.race([
        fetchAuthSession(),
        new Promise<undefined>((_, reject) =>
          setTimeout(() => reject(new Error('fetchAuthSession timeout')), 5000)
        ),
      ]);
    } catch {
      session = undefined;
    }

    let accessToken = session?.tokens?.accessToken;
    let idToken = session?.tokens?.idToken;
    const idTokenStr = idToken?.toString();

    // If the idToken is expired, force a fresh session
    if (idTokenStr && isJwtExpired(idTokenStr)) {
      try {
        const fresh = await Promise.race([
          fetchAuthSession({ forceRefresh: true }),
          new Promise<undefined>((_, reject) =>
            setTimeout(() => reject(new Error('fetchAuthSession refresh timeout')), 5000)
          ),
        ]);
        if (fresh?.tokens) {
          accessToken = fresh.tokens.accessToken ?? accessToken;
          idToken = fresh.tokens.idToken ?? idToken;
        }
      } catch {
        // Keep the original token — the API call will fail with 401 and
        // the error handler will surface it. Better than a stale-token loop.
      }
    }

    let groups: string[] = [];
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.toString().split('.')[1])) as Record<string, unknown>;
        groups = (payload['cognito:groups'] as string[] | undefined) ?? [];
      } catch {
        groups = [];
      }
    }

    let emailVerified = false;
    if (idToken) {
      try {
        const payload = JSON.parse(atob(idToken.toString().split('.')[1])) as Record<string, unknown>;
        const issuer = payload.iss as string | undefined;
        emailVerified = payload.email_verified === true || cfg.isLocal || (issuer?.includes('localhost') ?? false);
      } catch {
        emailVerified = false;
      }
    }

    return {
      user: {
        userId: user.userId,
        email: user.signInDetails?.loginId ?? '',
        emailVerified,
        groups,
      },
      idToken: idToken?.toString() ?? null,
    };
  } catch {
    return { user: null, idToken: null };
  }
}

export function CognitoAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      try {
        const result = await Promise.race([
          extractUserFromSession(),
          new Promise<{ user: null; idToken: null }>((resolve) => setTimeout(() => resolve({ user: null, idToken: null }), 15000)),
        ]);
        if (cancelled) return;
        if (result.user) {
          setUser(result.user);
        }
        setIdToken(result.idToken);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void initAuth();

    return () => { cancelled = true; };
  }, []);

  const handleSignIn = async (email: string, password: string) => {
    try {
      await signIn({
        username: email,
        password,
        ...(cfg.isLocal
          ? { options: { authFlowType: 'USER_PASSWORD_AUTH' as const } }
          : {}),
      });

      const result = await extractUserFromSession();
      if (result.user) {
        setUser(result.user);
      }
      setIdToken(result.idToken);
      return { error: null, idToken: result.idToken };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign in failed');
      if (error.name === 'UserNotConfirmedException') {
        return { error, needsConfirmation: true };
      }
      return { error };
    }
  };

  const handleSignUp = async (email: string, password: string, name?: string) => {
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...(name ? { name } : {}),
          },
        },
      });
      return { error: null, needsConfirmation: true };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign up failed');
      return { error };
    }
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Confirmation failed');
      return { error };
    }
  };

  const handleResendConfirmationCode = async (email: string) => {
    try {
      await resendSignUpCode({ username: email });
      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Resend failed');
      return { error };
    }
  };

  const handleSignOut = async () => {
    await amplifySignOut();
    setUser(null);
    setIdToken(null);
  };

  const refreshUser = async () => {
    const result = await extractUserFromSession();
    if (result.user) {
      setUser(result.user);
    }
    setIdToken(result.idToken);
  };

  const value = useMemo(
    () => ({
      user,
      idToken,
      loading,
      signIn: handleSignIn,
      signUp: handleSignUp,
      confirmSignUp: handleConfirmSignUp,
      resendConfirmationCode: handleResendConfirmationCode,
      signOut: handleSignOut,
      refreshUser,
    }),
    [user, idToken, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCognitoAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useCognitoAuth must be used within a CognitoAuthProvider');
  }
  return context;
}
